import { Fragment, useEffect, useMemo, useState } from "react";
import { api, ApiError, type AdhocCallSummary, type FlowTemplate } from "../../api/client";
import { flowErrorBadge, flowErrorSummary } from "./flowError";
import { keyPressRows, keyPressSummary } from "./keyPresses";
import { Icon } from "../../components/Icon";
import { durationSeconds, matchesQuery, pageNumbers, paginate, sortRows, type SortDir, type SortKey } from "./resultsTable";

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

const POLL_INTERVAL_MS = 2000;

type ResultTone = "pending" | "ok" | "warn" | "fail";

function result(row: AdhocCallSummary): { label: string; tone: ResultTone } {
  if (row.status === "pending") return { label: "Pending", tone: "pending" };
  if (row.status === "failed") return { label: row.error ? `Not placed: ${row.error}` : "Not placed", tone: "fail" };
  // status === "dispatched" from here: a real call row exists, but it may
  // not have reached a terminal state yet.
  if (row.call_status !== "terminal") return { label: "In progress", tone: "pending" };
  if (row.terminal_reason !== "completed") {
    return { label: `Failed${row.terminal_reason ? ` (${row.terminal_reason})` : ""}`, tone: "fail" };
  }
  // A call can reach terminal_reason "completed" with its flow having done
  // nothing at all: every node takes its error path, the graph routes each
  // one to hangup, and the call ends "normally". That is precisely how a
  // real deployed regression stayed invisible for a day (docs/PROGRESS.md
  // Slice CD/CE) — this row rendered a green "Completed".
  //
  // Checked BEFORE the AMD switch below, because every branch of it returns
  // an "ok" or a merely-informational tone. Warn, not fail: the call itself
  // genuinely did connect and complete, and some of the flow may well have
  // run — overstating it as a failure would be its own inaccuracy.
  const flowErrorLabel = flowErrorBadge(row);
  if (flowErrorLabel) {
    return { label: `Completed, but ${flowErrorLabel}`, tone: "warn" };
  }
  switch (row.amd_result) {
    case "HUMAN":
      return { label: "Live", tone: "ok" };
    case "MACHINE":
      return { label: "Machine", tone: "warn" };
    case "NOTSURE":
      return { label: "Notsure", tone: "warn" };
    default:
      // NOT_RUN: a genuinely completed call, just one the AMD checkbox was
      // never ticked for — labeled to say so explicitly, not left reading
      // like a fourth, ambiguous AMD outcome.
      return { label: "Completed (no AMD)", tone: "ok" };
  }
}

function formatDuration(row: AdhocCallSummary): string {
  const seconds = durationSeconds(row);
  return seconds === null ? "—" : `${seconds.toFixed(1)}s`;
}

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "to", label: "To" },
  { key: "from", label: "From" },
  { key: "trunk", label: "Trunk" },
  { key: "placed", label: "Placed" },
  { key: "duration", label: "Duration" },
  { key: "result", label: "Result" },
];

const PAGE_SIZES = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 10;

const resultLabel = (row: AdhocCallSummary) => result(row).label;

function detailRows(row: AdhocCallSummary): Array<[string, string]> {
  const rows: Array<[string, string]> = [
    ["Adhoc call ID", row.adhoc_call_id],
    ["Call ID", row.call_id ?? "— (not yet dispatched)"],
    ["Destination (to)", row.destination],
    ["Caller ID (from)", row.caller_id || "— (none set)"],
    ["Trunk", row.trunk_ref],
    ...(row.template_name
      ? ([["Template", `${row.template_name} (v${row.version_number ?? "?"})`]] as Array<[string, string]>)
      : []),
    ["Request status", row.status],
    ...(row.uid ? ([["API uid", row.uid]] as Array<[string, string]>) : []),
    ...(row.type ? ([["API type", row.type]] as Array<[string, string]>) : []),
    ["Created", new Date(row.created_at).toLocaleString()],
  ];
  if (row.dispatched_at) rows.push(["Dispatched", new Date(row.dispatched_at).toLocaleString()]);
  if (row.answered_at) rows.push(["Answered", new Date(row.answered_at).toLocaleString()]);
  if (row.ended_at) rows.push(["Ended", new Date(row.ended_at).toLocaleString()]);
  if (row.call_status) rows.push(["Call status", row.call_status]);
  if (row.terminal_reason) rows.push(["Terminal reason", row.terminal_reason]);
  if (row.hangup_cause) rows.push(["Hangup cause", row.hangup_cause]);
  if (row.amd_result) rows.push(["AMD result", row.amd_result]);
  if (row.amd_cause) rows.push(["AMD cause", row.amd_cause]);
  if (row.error) rows.push(["Error", row.error]);
  // Last, and unconditional on terminal_reason: a flow error is the one
  // field here that CONTRADICTS the outcome above it — the call says
  // "completed" while part of the flow never ran.
  const flowError = flowErrorSummary(row);
  if (flowError) rows.push(["Flow error", flowError]);
  return rows;
}

/**
 * Read-only results list for the test-call page: every ad hoc request this
 * tenant has placed, newest first, joined with its own real call's outcome
 * once the dispatcher has acted. GET /v1/adhoc-calls — the "to"/"from"
 * fields come from the request itself (set once, at placement, never
 * change); everything else comes from the real calls/call_attempts rows
 * once they exist. Each row expands in place to show everything the API
 * returns, including fields not worth a dedicated column (amd_cause, the
 * raw hangup_cause, every timestamp) — a lightweight debugging view, not a
 * second page.
 *
 * Polls while anything is still pending/in-progress, the same "don't make
 * someone manually refresh to see a call finish" pattern AdhocCallPanel
 * itself already uses, and stops once every visible row has settled. The
 * Refresh button re-fetches on demand (and restarts that polling), for a
 * call placed from another tab or by another admin after the list settled.
 *
 * Shown as a data table: search, sort by any column and pagination all run
 * client-side over the list the API returned (resultsTable.ts).
 */
export function CallResultsPanel() {
  const [rows, setRows] = useState<AdhocCallSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [templates, setTemplates] = useState<FlowTemplate[]>([]);
  const [flowId, setFlowId] = useState("");
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("placed");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);

  const visible = useMemo(() => {
    const matching = (rows ?? []).filter((row) => matchesQuery(row, resultLabel(row), query));
    return sortRows(matching, sortKey, sortDir, resultLabel);
  }, [rows, query, sortKey, sortDir]);
  const current = paginate(visible, page, pageSize);

  // The filter's options. A failure here is not worth surfacing: the
  // results themselves still load, just without the per-template filter.
  useEffect(() => {
    let cancelled = false;
    api
      .listTemplates()
      .then(({ templates }) => {
        if (!cancelled) setTemplates(templates);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const poll = () => {
      api
        .listAdhocCalls(flowId || undefined)
        .then(({ adhoc_calls }) => {
          if (cancelled) return;
          setRows(adhoc_calls);
          setError(null);
          setLoadedAt(new Date());
          setRefreshing(false);
          const stillSettling = adhoc_calls.some(
            (r) => r.status === "pending" || (r.status === "dispatched" && r.call_status !== "terminal"),
          );
          if (stillSettling) timer = window.setTimeout(poll, POLL_INTERVAL_MS);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(describeError(err));
          setRefreshing(false);
        });
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [refreshKey, flowId]);

  const refresh = () => {
    setRefreshing(true);
    setRefreshKey((key) => key + 1);
  };

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Newest/longest first is the useful default for time columns.
      setSortDir(key === "placed" || key === "duration" ? "desc" : "asc");
    }
    setPage(1);
  };

  const toggle = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section aria-labelledby="call-results-heading" className="card">
      <div className="card__header">
        <div>
          <h2 id="call-results-heading">Test call results</h2>
          <p className="card__subtitle">
            {loadedAt ? `Updated ${loadedAt.toLocaleTimeString()}` : "Loading the latest calls"}
          </p>
        </div>
        <button
          type="button"
          className="btn--outline btn--icon"
          onClick={refresh}
          disabled={refreshing || rows === null}
        >
          <Icon name="refresh" size={18} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <p className="card__note">
        Every call placed from the Test call page, newest first. Updates automatically while a
        call is still in progress. Click a row for full details.
      </p>

      {error ? (
        <p className="card__error" role="alert">
          {error}
        </p>
      ) : null}

      {rows === null ? (
        <p className="card__note">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="card__note">
          {flowId ? "No calls for this template yet." : "No test calls placed yet."}
          {flowId ? (
            <>
              {" "}
              <button type="button" className="btn--outline btn--sm" onClick={() => setFlowId("")}>
                Show all templates
              </button>
            </>
          ) : null}
        </p>
      ) : (
        <>
          <div className="datatable__toolbar">
            <label className="datatable__page-size">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span>entries</span>
            </label>
            {templates.length > 0 ? (
              <label className="datatable__page-size">
                <span>Template</span>
                <select
                  value={flowId}
                  onChange={(event) => {
                    setFlowId(event.target.value);
                    setPage(1);
                  }}
                  aria-label="Filter results by template"
                >
                  <option value="">All templates</option>
                  {templates.map((template) => (
                    <option key={template.flow_id} value={template.flow_id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="datatable__search">
              <span className="datatable__search-icon">
                <Icon name="search" size={18} />
              </span>
              <input
                type="search"
                placeholder="Search number, trunk, result, call ID…"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
                aria-label="Search call results"
              />
            </div>
          </div>

          <div className="datatable__scroll">
            <table className="results-table">
              <thead>
                <tr>
                  <th aria-hidden="true"></th>
                  {COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      aria-sort={
                        sortKey === column.key ? (sortDir === "asc" ? "ascending" : "descending") : undefined
                      }
                    >
                      <button
                        type="button"
                        className={`datatable__sort${sortKey === column.key ? " datatable__sort--active" : ""}`}
                        onClick={() => sortBy(column.key)}
                      >
                        {column.label}
                        <span className="datatable__sort-icon" aria-hidden="true">
                          {sortKey === column.key ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {current.rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="datatable__empty">
                      No calls match &ldquo;{query}&rdquo;.
                    </td>
                  </tr>
                ) : null}
                {current.rows.map((row) => {
                  const r = result(row);
                  const isOpen = expanded.has(row.adhoc_call_id);
                  return (
                    <Fragment key={row.adhoc_call_id}>
                      <tr
                        className="results-table__row"
                        onClick={() => toggle(row.adhoc_call_id)}
                        aria-expanded={isOpen}
                      >
                        <td className="results-table__chevron">{isOpen ? "▾" : "▸"}</td>
                        <td>{row.destination}</td>
                        <td>{row.caller_id || "—"}</td>
                        <td>{row.trunk_ref}</td>
                        <td>{new Date(row.created_at).toLocaleString()}</td>
                        <td>{formatDuration(row)}</td>
                        <td>
                          <span className={`results-table__badge results-table__badge--${r.tone}`}>{r.label}</span>
                          {/* The digits inline on the collapsed row, so a
                              menu call's outcome is scannable without
                              opening every row; the prompt each one
                              answered is in the detail below. */}
                          {keyPressSummary(row.key_presses) === "" ? null : (
                            <span className="results-table__keys-inline">
                              {" "}
                              key {keyPressSummary(row.key_presses)}
                            </span>
                          )}
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="results-table__detail-row">
                          <td colSpan={COLUMNS.length + 1}>
                            <dl className="results-table__detail">
                              {detailRows(row).map(([label, value]) => (
                                <div key={label} className="results-table__detail-item">
                                  <dt>{label}</dt>
                                  <dd>{value}</dd>
                                </div>
                              ))}
                            </dl>
                            {keyPressRows(row.key_presses).length > 0 ? (
                              <div className="results-table__keys">
                                <h4>What the caller pressed</h4>
                                <ol>
                                  {keyPressRows(row.key_presses).map((press, i) => (
                                    <li key={`${press.nodeId}:${i}`}>
                                      <strong>{press.action}</strong>
                                      {press.attempts === "" ? null : (
                                        <span className="results-table__keys-attempts"> {press.attempts}</span>
                                      )}
                                      {" — "}
                                      <span className="results-table__keys-prompt">“{press.prompt}”</span>{" "}
                                      <code>{press.nodeId}</code>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="datatable__footer">
            <p className="datatable__summary">
              Showing {current.first} to {current.last} of {current.total} entries
              {query ? ` (filtered from ${rows.length})` : ""}
            </p>
            <nav className="datatable__pages" aria-label="Results pages">
              <button
                type="button"
                className="btn--outline"
                onClick={() => setPage(current.page - 1)}
                disabled={current.page <= 1}
              >
                Previous
              </button>
              {pageNumbers(current.page, current.pageCount).map((n, i) =>
                n === null ? (
                  <span key={`gap-${i}`} className="datatable__gap" aria-hidden="true">
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    className={`datatable__page${n === current.page ? " datatable__page--active" : ""}`}
                    aria-current={n === current.page ? "page" : undefined}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ),
              )}
              <button
                type="button"
                className="btn--outline"
                onClick={() => setPage(current.page + 1)}
                disabled={current.page >= current.pageCount}
              >
                Next
              </button>
            </nav>
          </div>
        </>
      )}
    </section>
  );
}
